import { HexalInstance } from '@common/enums';
import { ITopic, ITopicGroup, TNullable, TopicStatus } from '@common/types';
import { StaticAutocompleteInput } from '@components/static-autocomplete-input';
import { Button } from '@components/ui/button';
import { TOPICS_DROPDOWN_SELECTOR_MIN } from '@constants/app';
import { cn, CURRENT_INSTANCE } from '@lib/utils';
import { useTranslation } from 'react-i18next';

interface ISelectTopicProps {
  topics: ITopic[];
  topicGroup: TNullable<ITopicGroup>;
  selectTopic: (topicId: string) => void;
  onCancel?: () => void;
}

function SelectTopic(props: ISelectTopicProps) {
  const { t } = useTranslation();
  const activeTopics = props.topics.filter((topic) => TopicStatus.Active === topic.status);
  const availableTopics = props.topicGroup
    ? activeTopics.filter((topic) => topic.groupId === props.topicGroup?.id)
    : activeTopics;
  return (
    <div className="mt-6 text-sm">
      <p className="mb-3">
        {props.topicGroup
          ? t('chat.topic_select.not_found_topic_group')
          : props.onCancel
            ? t('chat.topic_select.manual')
            : t('chat.topic_select.not_found')}
      </p>
      {availableTopics.length > TOPICS_DROPDOWN_SELECTOR_MIN ? (
        <div className="sm:min-h-72 min-h-80">
          <div className="flex items-start gap-2 w-full max-w-md">
            <div className="w-64 flex-1">
              <StaticAutocompleteInput
                suggestions={availableTopics.map((topic) => topic.displayName)}
                placeholder={t('common.search_topics')}
                onSelect={(v) => {
                  const topic = availableTopics.find((topic) => topic.displayName === v);
                  if (topic) {
                    props.selectTopic(topic.id);
                  }
                }}
                id="topic-selector"
                autoComplete="off"
                autoFocus
              />
            </div>
            {props.onCancel && (
              <Button
                variant="outline"
                className="border-white bg-white text-xircle-main hover:bg-slate-100 hover:text-xircle-main"
                onClick={props.onCancel}
              >
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          {props.onCancel && (
            <div className="mb-3 flex justify-end">
              <Button
                variant="outline"
                className="border-white bg-white text-xircle-main hover:bg-slate-100 hover:text-xircle-main"
                onClick={props.onCancel}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
          {availableTopics.map((topic) => (
            <Button
              key={topic.id}
              className={cn(
                'uppercase bg-xircle-secondary mr-2 mb-3 hover:shadow-md',
                CURRENT_INSTANCE.name === HexalInstance.Default &&
                  'text-hexal-dark-text-color hover:text-slate-200'
              )}
              onClick={() => props.selectTopic(topic.id)}
            >
              {topic.displayName || topic.name}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SelectTopic;
